import { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  HStack,
  VStack,
  Text,
  Box,
  Icon,
} from "@chakra-ui/react";
import { Info } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";

export function UpdatePrompt() {
  const [isDismissed, setIsDismissed] = useState(false); // Track if user clicked "Later"

  console.log("[UpdatePrompt] Component mounted");

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | undefined) {
      console.log("[UpdatePrompt] SW Registered:", r);
      console.log("[UpdatePrompt] SW Script URL:", r?.active?.scriptURL);
      // Check for updates every hour
      r &&
        setInterval(
          () => {
            console.log("[UpdatePrompt] Checking for updates...");
            r.update();
          },
          60 * 60 * 1000,
        );
    },
    onRegisterError(error: Error) {
      console.error("[UpdatePrompt] SW registration error:", error);
    },
  });

  // Check for updates when app is reopened for mobile PWA
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[UpdatePrompt] App resumed, checking for updates');
        navigator.serviceWorker.getRegistration().then(reg => {
          if (reg) {
            reg.update();
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const close = () => {
    console.log("[UpdatePrompt] User clicked 'Later' - showing icon badge");
    setNeedRefresh(false);
    setIsDismissed(true); // Show icon badge instead
  };

  const handleUpdate = () => {
    console.log(
      "[UpdatePrompt] User clicked 'Update Now' - reloading with new version...",
    );
    updateServiceWorker(true);
  };

  const showReload = needRefresh;

  useEffect(() => {
    if (needRefresh) {
      console.log("[UpdatePrompt] 🔔 New version available!");
    }
  }, [needRefresh]);

  if (!showReload) return null;

  console.log("[UpdatePrompt] 🔔 Rendering update prompt UI");

  // Small icon badge in top-center (after user clicks "Later")
  if (isDismissed) {
    return (
      <Box
        position="fixed"
        top={3}
        left="50%"
        transform="translateX(-50%)"
        zIndex={9999}
        bg="blue.500"
        color="white"
        borderRadius="full"
        w={10}
        h={10}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="lg"
        cursor="pointer"
        onClick={() => setIsDismissed(false)}
        _hover={{
          bg: "blue.600",
          transform: "translateX(-50%) scale(1.1)",
          boxShadow: "xl",
        }}
        transition="all 0.2s ease-in-out"
        title="New version available - Click to update"
        border="2px solid"
        borderColor="white"
      >
        <Icon as={Info} boxSize="20px" strokeWidth={2.5} />
      </Box>
    );
  }

  // Initial centered modal
  return (
    <>
      {/* Semi-transparent backdrop */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.400"
        zIndex={9998}
        onClick={close}
      />

      {/* Update prompt centered on screen */}
      <Alert
        status="info"
        variant="subtle"
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        maxW="450px"
        w="90%"
        borderRadius="xl"
        zIndex={9999}
        boxShadow="2xl"
        border="2px solid"
        borderColor="blue.300"
        bg="white"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        p={8}
      >
        <VStack spacing={4} w="full">
          <AlertIcon color="blue.500" boxSize="24px" />
          <VStack spacing={2}>
            <AlertTitle fontSize="md" color="gray.800" fontWeight="semibold">
              New version available!
            </AlertTitle>
            <Text fontSize="sm" color="gray.600">
              A new version of the application is ready. Update now to get the
              latest features and improvements.
            </Text>
          </VStack>
          <AlertDescription w="full">
            <HStack spacing={2} justify="center" w="full">
              <Button size="sm" colorScheme="blue" onClick={handleUpdate}>
                Update Now
              </Button>
              <Button
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={close}
              >
                Later
              </Button>
            </HStack>
          </AlertDescription>
        </VStack>
      </Alert>
    </>
  );
}
